import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { initI18n } from "@/lib/i18n";
import { createQueryClient } from "@/lib/query";
import { startTelemetry } from "@/lib/telemetry";
import { startTheme } from "@/lib/theme";
import { routeTree } from "@/routeTree.gen";
import "@/styles/globals.css";

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // The query cache is the cache. Letting the router keep its own copy of
  // loader data means two answers to "what is the current session".
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Before render: the tracer has to be registered for the first fetch to
// carry a `traceparent`, and the theme class has to be on <html> or the
// first paint is a flash of the wrong one.
startTelemetry();
startTheme();

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root missing from index.html");
}

// The UI language is corrected to the server's negotiated locale as soon
// as the session loads; this is only what renders before that answer
// arrives.
await initI18n();

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
